import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '@core/services/api/auth.service';
import { PreviewSessionService } from '@core/services/preview-session.service';
import { ThreadService } from '@core/services/api/thread.service';
import { ViewportService } from '@core/services/viewport.service';

/**
 * Resolves `/threads/:threadId` deep links (used by mention / thread-engagement
 * email notifications for non-task discussions) to the correct in-app
 * destination. There is no dedicated `/threads/:threadId` page — this guard
 * always returns a `UrlTree`, it never renders.
 *
 * - Anonymous: remember the intent and send to login (same mechanism as
 *   `minRoleGuard`); after logging in, the guard re-runs on this same URL and
 *   falls into the authenticated branch below. No anonymous preview here —
 *   only task discussions get that (see `taskDoAccessGuard`).
 * - Authenticated: resolve the thread, then redirect to its real target
 *   (`/tasks/do/:taskId` for TASK threads, `/diskussionen` otherwise).
 */
export const threadDeeplinkGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const preview = inject(PreviewSessionService);
  const threads = inject(ThreadService);
  const viewport = inject(ViewportService);
  const router = inject(Router);

  const threadId = route.paramMap.get('threadId');
  if (!threadId) {
    return router.createUrlTree(['/']);
  }

  if (!auth.isAuthenticated()) {
    return preview.loginUrlTree(state.url);
  }

  return threads.getThread(threadId).pipe(
    map((thread) => {
      if (thread.targetType === 'TASK') {
        return router.createUrlTree(['/tasks/do', thread.targetId]);
      }

      return viewport.isMobile()
        ? router.createUrlTree(['/diskussionen', threadId])
        : router.createUrlTree(['/diskussionen'], { queryParams: { threadId } });
    }),
    catchError(() => of(router.createUrlTree(['/']))),
  );
};
